// La ficha de un producto tal como Tiendanube la espera, y su validación.
//
// ADR-2026-09-06-lanzamientos-ficha-completa. Todo lo que hay acá es lógica pura:
// convierte lo que se tipeó en el formulario al vocabulario y las unidades de la
// API, y dice qué falta antes de intentar publicar. Sin red y sin React, para
// poder probarlo.
//
// Reglas que salen de la documentación de Tiendanube, no de nuestra cabeza:
//   · `cost` tiene que ser mayor que cero, o se omite
//   · `weight` en kilogramos; `width`/`height`/`depth` en centímetros
//   · `tags` es UNA cadena separada por comas, no un array
//   · `seo_title` 70 caracteres, `seo_description` 320
//   · `attributes` máximo 3 por producto
//   · cada variante manda un `values` por atributo, en el mismo orden
//
// El precio promocional NO se expone aunque la API lo acepte y la columna exista:
// un pre-lanzamiento arranca a precio de lista y la oferta se arma después en la
// tienda. Es una decisión de producto, no un límite técnico.

import { nombreCanal, reglasDe, type ReglasDelCanal } from "./canales";

/** Tope de atributos por producto que impone Tiendanube. */
export const MAX_ATRIBUTOS = 3;
export const MAX_SEO_TITULO = 70;
export const MAX_SEO_DESCRIPCION = 320;

export interface VarianteFicha {
  id: string;
  orden: number;
  /** Un valor por atributo del producto, en el mismo orden: ["Rojo", "M"]. */
  valores: string[];
  precio: string | null;
  costo: string | null;
  stock: string | null;
  sku: string | null;
  codigoBarras: string | null;
  peso: string | null;
  alto: string | null;
  ancho: string | null;
  profundidad: string | null;
  /**
   * Etiqueta de la foto que Tiendanube muestra al elegir esta variante.
   *
   * Es la MISMA con la que viajan las imágenes en el payload del export
   * (`imagen_generada_id`, que para una foto subida a mano es su `file_name`):
   * el worker linkea la variante buscando esa clave en el mapa de lo que subió.
   */
  imagenGeneradaId?: string | null;
}

export interface FichaProducto {
  nombre: string;
  descripcion: string | null;
  precio: string | null;
  costo: string | null;
  stock: string | null;
  sku: string | null;
  codigoBarras: string | null;
  peso: string | null;
  alto: string | null;
  ancho: string | null;
  profundidad: string | null;
  marca: string | null;
  tags: string | null;
  seoTitulo: string | null;
  seoDescripcion: string | null;
  /** Nombres de los atributos, en orden. Vacío = producto de una sola variante. */
  atributos: string[];
  variantes: VarianteFicha[];
  /** Los caminos de sus categorías, ya resueltos por quien arma la ficha. */
  categorias?: NivelDeCategoria[][];
  /**
   * Qué define cada variante por su cuenta. Apagado NO significa vacío: significa
   * que la variante usa el valor general del producto. El producto siempre tiene
   * los suyos, así que prender y apagar un interruptor no pierde nada.
   */
  precioPorVariante?: boolean;
  envioPorVariante?: boolean;
  /** Tiendanube linkea UNA imagen por variante. */
  imagenesPorVariante?: boolean;
}

export interface ProblemaDeFicha {
  campo: string;
  mensaje: string;
}

// ── Números del formulario ─────────────────────────────────────────────────

/**
 * Un campo numérico tipeado a mano. Acepta la coma decimal del teclado en
 * español: sin esto, "0,5" kg llegaría como NaN y el envío se calcularía mal.
 */
export function aNumero(valor: string | null | undefined): number | null {
  if (valor === null || valor === undefined) return null;
  const t = String(valor).trim().replace(",", ".");
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** El texto tal cual, o `null` si quedó vacío. */
function texto(v: string | null | undefined): string | null {
  const t = (v ?? "").trim();
  return t === "" ? null : t;
}

// ── Margen ─────────────────────────────────────────────────────────────────

/**
 * Margen sobre el PRECIO DE VENTA, en porcentaje: cuánto de cada peso que entra
 * queda después de pagar el costo. Es la lectura habitual en un catálogo, y no
 * el markup sobre el costo (que con costo 600 y venta 1000 daría 67% en vez
 * de 40% y suena mejor de lo que es).
 *
 * Negativo es un resultado válido y hay que verlo: se está vendiendo a pérdida.
 */
export function margenDeGanancia(
  precio: string | null | undefined,
  costo: string | null | undefined,
): number | null {
  const c = aNumero(costo);
  const venta = aNumero(precio);
  if (c === null || venta === null || venta === 0) return null;
  return ((venta - c) / venta) * 100;
}

// ── Validación ─────────────────────────────────────────────────────────────

/**
 * Qué le falta a la ficha para poder publicarse. Vacío = está lista.
 *
 * Las reglas son las del DESTINO del lanzamiento (`reglasDe(canal)`): la misma
 * ficha puede estar lista para Tiendanube y no para VTEX, que exige el paquete y
 * un SKU por variante. Sin reglas, valida como Tiendanube, que es lo que hacía
 * siempre.
 */
export function problemasDeFicha(
  f: FichaProducto,
  reglas: ReglasDelCanal = reglasDe("tiendanube"),
): ProblemaDeFicha[] {
  const p: ProblemaDeFicha[] = [];
  const conVariantes = f.atributos.length > 0;
  const plataforma = nombreCanal(reglas.canal);

  if (!texto(f.nombre)) p.push({ campo: "nombre", mensaje: "falta el nombre" });
  else if (reglas.maxNombre !== null && f.nombre.trim().length > reglas.maxNombre) {
    p.push({ campo: "nombre", mensaje: `el nombre supera ${reglas.maxNombre} caracteres` });
  }

  if (reglas.maxAtributos !== null && f.atributos.length > reglas.maxAtributos) {
    p.push({
      campo: "atributos",
      mensaje: `${plataforma} admite hasta ${reglas.maxAtributos} atributos por producto`,
    });
  }
  if (reglas.maxVariantes !== null && f.variantes.length > reglas.maxVariantes) {
    p.push({
      campo: "variantes",
      mensaje: `${plataforma} admite hasta ${reglas.maxVariantes} variantes por producto`,
    });
  }

  // Un destino que no vende no tiene por qué tener precio: pedírselo a alguien
  // que sólo quiere ordenar sus fotos en carpetas es pedirle un dato para
  // tirarlo (ADR-2026-09-14-lanzamiento-a-carpetas).
  const pidePrecio = reglas.conPrecioYStock;

  if (!conVariantes) {
    // Sin atributos el producto ES una sola variante: su precio es obligatorio.
    if (pidePrecio && aNumero(f.precio) === null) {
      p.push({ campo: "precio", mensaje: "falta el precio" });
    }
  } else {
    // Con variantes el precio general sigue siendo el piso del que heredan; solo
    // se puede omitir si TODAS definen el suyo.
    const todasTienenPrecio =
      f.precioPorVariante === true &&
      f.variantes.length > 0 &&
      f.variantes.every((v) => aNumero(v.precio) !== null);
    if (pidePrecio && aNumero(f.precio) === null && !todasTienenPrecio) {
      p.push({ campo: "precio", mensaje: "falta el precio" });
    }
    if (f.variantes.length === 0) {
      p.push({ campo: "variantes", mensaje: "declaraste atributos pero no cargaste variantes" });
    }
    const combinaciones = new Set<string>();
    // Solo si las variantes definen su propio precio. Con el interruptor apagado
    // heredan el del producto, que ya se validó arriba.
    if (pidePrecio && f.precioPorVariante === true && aNumero(f.precio) === null) {
      for (const v of f.variantes) {
        if (aNumero(v.precio) === null) {
          p.push({ campo: "variante.precio", mensaje: "hay una variante sin precio" });
          break;
        }
      }
    }
    for (const v of f.variantes) {
      const valores = v.valores.map((x) => texto(x));
      if (valores.length !== f.atributos.length || valores.some((x) => x === null)) {
        p.push({
          campo: "variante.valores",
          mensaje: `cada variante necesita un valor por atributo (${f.atributos.join(", ")})`,
        });
        break;
      }
      const clave = valores.join(" · ").toLowerCase();
      if (combinaciones.has(clave)) {
        p.push({ campo: "variante.valores", mensaje: `la combinación "${valores.join(" · ")}" está repetida` });
        break;
      }
      combinaciones.add(clave);
    }
  }

  // ── Lo que exige el destino y Tiendanube no ──────────────────────────────

  if (reglas.skuOCodigoPorVariante) {
    p.push(...problemasDeIdentidad(f, conVariantes, plataforma, reglas.maxSku));
  } else if (reglas.maxSku !== null) {
    const largo = Math.max(
      (f.sku ?? "").trim().length,
      ...f.variantes.map((v) => (v.sku ?? "").trim().length),
    );
    if (largo > reglas.maxSku) {
      p.push({ campo: "sku", mensaje: `el SKU supera ${reglas.maxSku} caracteres` });
    }
  }

  if (reglas.envioObligatorio) {
    const faltaPaquete = (
      peso: string | null,
      alto: string | null,
      ancho: string | null,
      prof: string | null,
    ) => [peso, alto, ancho, prof].some((m) => (aNumero(m) ?? 0) <= 0);
    const productoIncompleto = faltaPaquete(f.peso, f.alto, f.ancho, f.profundidad);
    if (conVariantes && f.envioPorVariante === true) {
      // Con envío por variante, la que no define el suyo hereda el del producto.
      const alguna = f.variantes.some((v) =>
        faltaPaquete(
          v.peso ?? f.peso,
          v.alto ?? f.alto,
          v.ancho ?? f.ancho,
          v.profundidad ?? f.profundidad,
        ),
      );
      if (alguna) {
        p.push({
          campo: "variante.envio",
          mensaje: `hay una variante sin peso o medidas del paquete (${plataforma} los exige)`,
        });
      }
    } else if (productoIncompleto) {
      p.push({
        campo: "envio",
        mensaje: `faltan el peso y las medidas del paquete (${plataforma} los exige para crear el producto)`,
      });
    }
  }

  if (reglas.unaSolaCategoria && (f.categorias?.length ?? 0) > 1) {
    p.push({
      campo: "categorias",
      mensaje: `${plataforma} admite una sola categoría por producto: dejá la más específica`,
    });
  }

  if ((f.seoTitulo ?? "").length > reglas.maxSeoTitulo) {
    p.push({ campo: "seoTitulo", mensaje: `el título SEO supera ${reglas.maxSeoTitulo} caracteres` });
  }
  if ((f.seoDescripcion ?? "").length > reglas.maxSeoDescripcion) {
    p.push({
      campo: "seoDescripcion",
      mensaje: `la descripción SEO supera ${reglas.maxSeoDescripcion} caracteres`,
    });
  }
  return p;
}

/**
 * VTEX identifica cada SKU por su `RefId` (único) o su EAN: una variante sin
 * ninguno de los dos no se puede crear, y dos con el mismo SKU chocan.
 *
 * Con UNA variante el SKU general del producto alcanza (es la única que lo
 * hereda). Con varias, heredar el mismo SKU las haría iguales, así que cada una
 * necesita el suyo.
 */
function problemasDeIdentidad(
  f: FichaProducto,
  conVariantes: boolean,
  plataforma: string,
  maxSku: number | null,
): ProblemaDeFicha[] {
  const p: ProblemaDeFicha[] = [];
  const largoOk = (sku: string | null) => maxSku === null || sku === null || sku.length <= maxSku;

  if (!conVariantes || f.variantes.length <= 1) {
    const sku = texto(f.variantes[0]?.sku ?? null) ?? texto(f.sku);
    const codigo = texto(f.variantes[0]?.codigoBarras ?? null) ?? texto(f.codigoBarras);
    if (!sku && !codigo) {
      p.push({ campo: "sku", mensaje: `${plataforma} pide un SKU o un código de barras` });
    } else if (!largoOk(sku)) {
      p.push({ campo: "sku", mensaje: `el SKU supera ${maxSku} caracteres` });
    }
    return p;
  }

  const vistos = new Set<string>();
  for (const v of f.variantes) {
    const sku = texto(v.sku);
    const codigo = texto(v.codigoBarras);
    if (!sku && !codigo) {
      p.push({
        campo: "variante.sku",
        mensaje: `${plataforma} pide un SKU o un código de barras propio en cada variante`,
      });
      break;
    }
    if (!largoOk(sku)) {
      p.push({ campo: "variante.sku", mensaje: `hay un SKU de variante que supera ${maxSku} caracteres` });
      break;
    }
    if (sku) {
      const clave = sku.toLowerCase();
      if (vistos.has(clave)) {
        p.push({ campo: "variante.sku", mensaje: `el SKU "${sku}" está repetido en dos variantes` });
        break;
      }
      vistos.add(clave);
    }
  }
  return p;
}

// ── Traducción al contrato del backend ─────────────────────────────────────

export interface TnVarianteBody {
  valores: string[];
  precio: string;
  /** Foto asignada a esta variante. El backend la linkea DESPUÉS de subirla. */
  imagen_generada_id?: string;
  costo?: string;
  stock?: string;
  sku?: string;
  codigo_barras?: string;
  peso?: number;
  alto?: number;
  ancho?: number;
  profundidad?: number;
}

export interface TnProductoBody {
  nombre: string;
  precio: string;
  descripcion: string;
  stock: string;
  sku: string;
  costo?: string;
  codigo_barras?: string;
  peso?: number;
  alto?: number;
  ancho?: number;
  profundidad?: number;
  marca?: string;
  tags?: string;
  seo_titulo?: string;
  seo_descripcion?: string;
  atributos?: string[];
  variantes?: TnVarianteBody[];
  /**
   * A qué categorías va, como CAMINOS desde la raíz —
   * ADR-2026-09-11-categorias-al-publicar.
   *
   * El camino entero y no el id de la hoja: una categoría que todavía no existe
   * en Tiendanube hay que crearla allá, y para crear una subcategoría hace falta
   * el id de su madre, que quizás tampoco existe.
   */
  categorias?: NivelDeCategoria[][];
}

/** Un nivel del camino. `tnCategoryId` null = todavía no existe en la tienda. */
export interface NivelDeCategoria {
  id: string;
  nombre: string;
  tn_category_id: number | null;
}

/** `cost` tiene que ser > 0: cero o vacío se omiten en vez de viajar. */
function costoValido(v: string | null | undefined): string | undefined {
  const n = aNumero(v);
  return n !== null && n > 0 ? String(n) : undefined;
}

function medida(v: string | null | undefined): number | undefined {
  const n = aNumero(v);
  return n !== null ? n : undefined;
}

/** Etiquetas: de lo que se tipeó a la cadena separada por comas que espera la API. */
export function normalizarTags(v: string | null | undefined): string | undefined {
  const partes = (v ?? "")
    .split(",")
    .map((t) => t.trim())
    .filter((t) => t !== "");
  return partes.length > 0 ? partes.join(",") : undefined;
}

/**
 * Convierte la ficha al cuerpo que viaja al backend. Los campos vacíos se OMITEN
 * en vez de mandarse en cero o en blanco: un `cost: 0` o un `weight: 0` no son
 * "sin dato" para Tiendanube, son un dato malo.
 */
export function tnBodyDeProducto(f: FichaProducto): TnProductoBody {
  const conVariantes = f.atributos.length > 0 && f.variantes.length > 0;
  const body: TnProductoBody = {
    nombre: texto(f.nombre) ?? "Producto",
    // Con variantes el precio del producto no se usa (lo pone cada una), pero el
    // contrato lo pide igual: va el de la primera, o vacío.
    precio: texto(f.precio) ?? (conVariantes ? (texto(f.variantes[0]?.precio ?? null) ?? "") : ""),
    descripcion: texto(f.descripcion) ?? "",
    stock: texto(f.stock) ?? "",
    sku: texto(f.sku) ?? "",
  };

  const costo = costoValido(f.costo);
  if (costo) body.costo = costo;
  const barras = texto(f.codigoBarras);
  if (barras) body.codigo_barras = barras;

  const peso = medida(f.peso);
  if (peso !== undefined) body.peso = peso;
  const alto = medida(f.alto);
  if (alto !== undefined) body.alto = alto;
  const ancho = medida(f.ancho);
  if (ancho !== undefined) body.ancho = ancho;
  const prof = medida(f.profundidad);
  if (prof !== undefined) body.profundidad = prof;

  const marca = texto(f.marca);
  if (marca) body.marca = marca;
  const tags = normalizarTags(f.tags);
  if (tags) body.tags = tags;

  // Se RECORTA en vez de dejar que Tiendanube rechace el producto entero por un
  // título largo. La UI ya avisa antes de llegar acá.
  const seoT = texto(f.seoTitulo);
  if (seoT) body.seo_titulo = seoT.slice(0, MAX_SEO_TITULO);
  const seoD = texto(f.seoDescripcion);
  if (seoD) body.seo_descripcion = seoD.slice(0, MAX_SEO_DESCRIPCION);

  // Solo al crear, que es cuando existe este body: a un producto que ya está en
  // la tienda no se le tocan las categorías, igual que no se le toca la ficha.
  if (f.categorias?.length) body.categorias = f.categorias;

  if (conVariantes) {
    // Los interruptores deciden de dónde sale cada dato. Apagado = el valor
    // general del producto, que siempre está.
    const porPrecio = f.precioPorVariante === true;
    const porEnvio = f.envioPorVariante === true;
    body.atributos = f.atributos.map((a) => a.trim()).filter((a) => a !== "");
    body.variantes = [...f.variantes]
      .sort((a, b) => a.orden - b.orden)
      .map((v) => {
        const item: TnVarianteBody = {
          valores: v.valores.map((x) => (x ?? "").trim()),
          // Tiendanube pide precio en CADA variante: si no lo define ella, va el
          // del producto. Nunca queda una variante sin precio.
          precio: (porPrecio ? texto(v.precio) : null) ?? texto(f.precio) ?? "",
        };
        // La foto solo viaja si el producto separó imágenes por variante.
        if (f.imagenesPorVariante === true && v.imagenGeneradaId) {
          item.imagen_generada_id = v.imagenGeneradaId;
        }
        const vc = (porPrecio ? costoValido(v.costo) : undefined) ?? costo;
        if (vc) item.costo = vc;
        // Stock y SKU son SIEMPRE de la variante, sin interruptor: tener stock y
        // código propios es la razón de existir de una variante. Un talle M no
        // comparte unidades con el L. Igual heredan del producto si están vacíos.
        const vs = texto(v.stock) ?? texto(f.stock);
        if (vs) item.stock = vs;
        const vsku = texto(v.sku) ?? texto(f.sku);
        if (vsku) item.sku = vsku;
        const vb = (porPrecio ? texto(v.codigoBarras) : null) ?? texto(f.codigoBarras);
        if (vb) item.codigo_barras = vb;
        const vpeso = (porEnvio ? medida(v.peso) : undefined) ?? peso;
        if (vpeso !== undefined) item.peso = vpeso;
        const valto = (porEnvio ? medida(v.alto) : undefined) ?? alto;
        if (valto !== undefined) item.alto = valto;
        const vancho = (porEnvio ? medida(v.ancho) : undefined) ?? ancho;
        if (vancho !== undefined) item.ancho = vancho;
        const vprof = (porEnvio ? medida(v.profundidad) : undefined) ?? prof;
        if (vprof !== undefined) item.profundidad = vprof;
        return item;
      });
  }

  return body;
}
